import React, { useState } from 'react';
import { TaskType, TaskStatus } from '../types';
import { X, Plus } from 'lucide-react';

interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (title: string, type: TaskType, dueDate: string, description: string) => void;
}

export const AddTaskModal: React.FC<AddTaskModalProps> = ({ isOpen, onClose, onAdd }) => {
  const [title, setTitle] = useState('');
  const [type, setType] = useState<TaskType>(TaskType.MANUAL);
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(title, type, dueDate, description);
    // Reset
    setTitle('');
    setType(TaskType.MANUAL);
    setDueDate('');
    setDescription('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">新增自定义职责</h3>
          <button onClick={onClose} className="flex items-center justify-center p-1 text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              职责名称 <span className="text-red-500">*</span>
            </label>
            <input 
              type="text" 
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-solid border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              placeholder="输入任务名称"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              职责类型
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { val: TaskType.MANUAL, label: '人工自查' },
                { val: TaskType.ACTION, label: '动作执行' },
                { val: TaskType.SYSTEM, label: '系统数据' },
              ].map((opt) => (
                <button
                  key={opt.val}
                  type="button"
                  onClick={() => setType(opt.val)}
                  className={`text-sm py-2 px-1 rounded-md border transition-colors ${
                    type === opt.val 
                      ? 'bg-primary text-white border-primary' 
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {type === TaskType.MANUAL && '仅需勾选确认完成。'}
              {type === TaskType.ACTION && '需要点击按钮进行表单填写。'}
              {type === TaskType.SYSTEM && '状态由系统后台自动更新，不可手动操作。'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              截止日期
            </label>
            <input 
              type="date" 
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full border border-solid border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              描述说明
            </label>
            <textarea 
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-solid border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              rows={2}
            ></textarea>
          </div>

          <div className="pt-4 flex space-x-3">
            <button 
              type="button" 
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
            >
              取消
            </button>
            <button 
              type="submit" 
              className="flex-1 py-2 px-4 bg-primary text-white rounded-lg font-medium hover:bg-slate-700 flex items-center justify-center"
            >
              <Plus size={16} className="mr-2" />
              添加
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};